// Static UI catalog.
//
// IDs and structural data are language-agnostic.
// Label maps & arrays use a live Proxy/getter pattern so they always reflect
// the active i18n language (it / en / future locales).
//
// Backward-compatible exports:
//   ROLES, ROLE_GROUPS, ROLE_IDS, ROLE_GROUP_LABELS,
//   BUSINESS_TYPES, BUSINESS_TYPE_IDS, BUSINESS_TYPE_LABELS,
//   TENANT_STATUSES, TENANT_STATUS_IDS, STATUS_LABELS,
//   MODULE_LABELS, PLAN_LABELS,
//   PERMISSION_MATRIX, MODULE_NAV_GROUPS
//
// New i18n helpers (preferred):
//   roleLabel(t, id), planLabel(t, id), moduleLabel(t, id),
//   businessTypeLabel(t, id), tenantStatusLabel(t, id), billingStatusLabel(t, id)

import i18n from "@/i18n";

// --- Pure IDs ---
export const ROLE_IDS = [
  "platform_owner",
  "platform_admin",
  "restaurant_owner",
  "hotel_owner",
  "restaurant_manager",
  "hotel_manager",
  "hotel_reception",
  "hotel_housekeeping",
  "staff_member",
  "accountant",
  "viewer",
];

export const ROLE_GROUPS = {
  platform_owner: "platform",
  platform_admin: "platform",
  restaurant_owner: "tenant",
  hotel_owner: "tenant",
  restaurant_manager: "tenant",
  hotel_manager: "tenant",
  hotel_reception: "tenant",
  hotel_housekeeping: "tenant",
  staff_member: "tenant",
  accountant: "tenant",
  viewer: "tenant",
};

export const PERMISSION_MATRIX = [
  { key: "manage_tenants", roles: ["platform_owner", "platform_admin"] },
  { key: "view_all_tenants", roles: ["platform_owner", "platform_admin"] },
  { key: "toggle_modules", roles: ["platform_owner", "platform_admin"] },
  {
    key: "manage_tenant_users",
    roles: [
      "platform_owner",
      "platform_admin",
      "restaurant_owner",
      "hotel_owner",
      "restaurant_manager",
      "hotel_manager",
    ],
  },
  {
    key: "view_billing",
    roles: [
      "platform_owner",
      "platform_admin",
      "restaurant_owner",
      "hotel_owner",
      "accountant",
    ],
  },
  {
    key: "operate_pos",
    roles: [
      "restaurant_owner",
      "restaurant_manager",
      "hotel_owner",
      "hotel_manager",
      "staff_member",
    ],
  },
  {
    key: "read_dashboards",
    roles: [
      "platform_owner",
      "platform_admin",
      "restaurant_owner",
      "hotel_owner",
      "restaurant_manager",
      "hotel_manager",
      "staff_member",
      "accountant",
      "viewer",
    ],
  },
];

export const BUSINESS_TYPE_IDS = ["restaurant", "hotel", "combo"];
export const TENANT_STATUS_IDS = ["active", "trial", "grace_period", "suspended", "cancelled"];
export const BILLING_STATUS_IDS = ["paid", "pending", "failed", "refunded"];

export const MODULE_NAV_GROUPS = [
  {
    id: "operations",
    items: ["pos", "reservations", "takeaway", "delivery", "payments", "room_service"],
  },
  { id: "supply", items: ["inventory", "recipes"] },
  {
    id: "intelligence",
    items: [
      "food_cost",
      "waste_control",
      "margin_analysis",
      "break_even",
      "business_analytics",
      "ai_forecast",
    ],
  },
  { id: "hotel_ops", items: ["breakfast_management", "revenue_management"] },
  { id: "growth", items: ["crm_marketing", "reports", "notifications"] },
];

const TENANT_STATUS_TONE = {
  active: "success",
  trial: "neutral",
  grace_period: "warning",
  suspended: "danger",
  cancelled: "neutral",
};

// --- i18n helpers (preferred API) ---
function safeT(t, key) {
  // Robust label resolution: works with both `useTranslation`'s t and i18n.t.
  const fn = typeof t === "function" ? t : (k) => i18n.t(k);
  const v = fn(key);
  return v && v !== key ? v : null;
}

export function moduleLabel(t, id) {
  return safeT(t, `modules_catalog.${id}`) || (id || "").replace(/_/g, " ");
}

export function planLabel(t, id) {
  return safeT(t, `plans_catalog.${id}`) || (id || "").replace(/_/g, " ");
}

export function roleLabel(t, id) {
  return safeT(t, `roles_list.${id}`) || (id || "").replace(/_/g, " ");
}

export function tenantStatusLabel(t, id) {
  return safeT(t, `tenant_status.${id}`) || id;
}

export function billingStatusLabel(t, id) {
  return safeT(t, `billing_status.${id}`) || id;
}

export function businessTypeLabel(t, id) {
  return safeT(t, `business_types.${id}`) || id;
}

// --- Live, language-aware label maps (backward-compat) ---
function liveLabelMap(prefix) {
  return new Proxy(
    {},
    {
      get(_target, key) {
        if (typeof key !== "string") return undefined;
        const v = i18n.t(`${prefix}.${key}`);
        return v && v !== `${prefix}.${key}` ? v : key;
      },
      has(_target, key) {
        if (typeof key !== "string") return false;
        const v = i18n.t(`${prefix}.${key}`);
        return v && v !== `${prefix}.${key}`;
      },
      ownKeys() {
        const res = i18n.getResource(i18n.resolvedLanguage || "it", "translation", prefix);
        return res ? Object.keys(res) : [];
      },
      getOwnPropertyDescriptor(_target, key) {
        return { enumerable: true, configurable: true };
      },
    }
  );
}

// Tenant + billing statuses merged (historical shape used by Billing/Settings/etc.).
function liveStatusMap() {
  const tenantPrefix = "tenant_status";
  const billingPrefix = "billing_status";
  const lookup = (key) => {
    if (typeof key !== "string") return undefined;
    const a = i18n.t(`${tenantPrefix}.${key}`);
    if (a && a !== `${tenantPrefix}.${key}`) return a;
    const b = i18n.t(`${billingPrefix}.${key}`);
    if (b && b !== `${billingPrefix}.${key}`) return b;
    return key;
  };
  return new Proxy(
    {},
    {
      get(_t, key) {
        return lookup(key);
      },
      has(_t, key) {
        return typeof key === "string" && !!lookup(key);
      },
      ownKeys() {
        const lang = i18n.resolvedLanguage || "it";
        const a = i18n.getResource(lang, "translation", "tenant_status") || {};
        const b = i18n.getResource(lang, "translation", "billing_status") || {};
        return Array.from(new Set([...Object.keys(a), ...Object.keys(b)]));
      },
      getOwnPropertyDescriptor() {
        return { enumerable: true, configurable: true };
      },
    }
  );
}

export const ROLE_GROUP_LABELS = liveLabelMap("role_groups");
export const BUSINESS_TYPE_LABELS = liveLabelMap("business_types");
export const MODULE_LABELS = liveLabelMap("modules_catalog");
export const PLAN_LABELS = liveLabelMap("plans_catalog");
export const TENANT_STATUS_LABELS = liveLabelMap("tenant_status");
export const BILLING_STATUS_LABELS = liveLabelMap("billing_status");
export const STATUS_LABELS = liveStatusMap();

// --- Live arrays (backward-compat) ---
// Each entry has `id` and a getter `label` that reads from the current language.
function liveArray(idList, labelPrefix, extra) {
  return idList.map((id) => {
    const obj = { id, ...(extra ? extra(id) : {}) };
    Object.defineProperty(obj, "label", {
      enumerable: true,
      configurable: false,
      get() {
        const v = i18n.t(`${labelPrefix}.${id}`);
        return v && v !== `${labelPrefix}.${id}` ? v : id;
      },
    });
    return obj;
  });
}

export const ROLES = liveArray(ROLE_IDS, "roles_list", (id) => ({
  group: ROLE_GROUPS[id],
}));

export const BUSINESS_TYPES = liveArray(BUSINESS_TYPE_IDS, "business_types");

export const TENANT_STATUSES = liveArray(TENANT_STATUS_IDS, "tenant_status", (id) => ({
  tone: TENANT_STATUS_TONE[id] || "neutral",
}));
